
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { getAuthEventName, isAdmin as checkIsAdmin } from "@/services/authService";

export const useAdminCheck = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Add a timeout to prevent indefinite loading
        const timeoutId = setTimeout(() => {
          console.error("Auth check timed out");
          setIsLoading(false);
          navigate("/admin/login");
        }, 5000);

        const isAdminUser = await checkIsAdmin();

        clearTimeout(timeoutId);

        if (!isAdminUser) {
          localStorage.removeItem("adminLoggedIn");
          throw new Error("Not an admin");
        }

        localStorage.setItem("adminLoggedIn", "true");
        setIsAdmin(true);
        setIsLoading(false);
      } catch (error) {
        console.error("Auth error:", error);
        
        // Only show toast if actively navigating from an admin page
        if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
          toast({
            title: "Access denied",
            description: "Please login to access the admin area",
            variant: "destructive",
          });
          
          // Add a small delay before navigation to prevent navigation loops
          setTimeout(() => {
            navigate("/admin/login");
          }, 100);
        }
        
        setIsAdmin(false);
        setIsLoading(false);
      }
    };
    
    checkAuth();
    
    const handleAuthChange = async () => {
      const isAdminUser = await checkIsAdmin();
      if (isAdminUser) {
        localStorage.setItem("adminLoggedIn", "true");
        setIsAdmin(true);
      } else {
        localStorage.removeItem("adminLoggedIn");
        setIsAdmin(false);
      }
    };

    const authEventName = getAuthEventName();
    window.addEventListener(authEventName, handleAuthChange);
    window.addEventListener("storage", handleAuthChange);
    
    return () => {
      window.removeEventListener(authEventName, handleAuthChange);
      window.removeEventListener("storage", handleAuthChange);
    };
  }, [navigate, toast]);

  return { isLoading, isAdmin };
};
